import Box from '@mui/material/Box';
import BugReportIcon from '@mui/icons-material/BugReport';
import SecurityIcon from '@mui/icons-material/Security';
import { getSecurityNavItemPath } from 'src/mindtime/config/securityNav';
import type { WazuhMalwareDetectionItem } from 'src/types/mindtime/wazuhMalware';
import {
  EDR_COLORS,
  EdrDateTime,
  EdrDetailEndpointSidebar,
  EdrDetailKeyValueRows,
  EdrDetailSectionCard,
  EdrExpandableText,
  EdrSeverityPill,
  formatEdrDateTime,
  type EdrEntityDetailPageProps,
  type EdrSeverityLevel,
} from '../shared/edrPrimitives';

const normalizeKey = (value: string | undefined | null): string =>
  (value ?? '').toString().trim().toLowerCase();

export const toMalwareTitle = (value: string | undefined | null): string => {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

export const malwareSeverityToLevel = (sev: string | undefined): EdrSeverityLevel => {
  const key = normalizeKey(sev);
  if (key === 'critical' || key === 'high' || key === 'medium' || key === 'low') return key;
  return 'medium';
};

/** Id for GET /malware/detections/{id} — prefers `id`, then `_id`. */
export const getMalwareDetectionId = (
  item: Pick<WazuhMalwareDetectionItem, 'id' | '_id'> | null | undefined
): string => String(item?.id ?? item?._id ?? '').trim();

/** Short page/header title for long rule-style threat names. */
export const formatMalwareThreatTitle = (threatName?: string | null): string => {
  const name = threatName?.trim() || 'Malware Detection';
  return name.length > 96 ? `${name.slice(0, 93)}…` : name;
};

export const buildMalwareDetailProps = (
  item: WazuhMalwareDetectionItem
): Omit<EdrEntityDetailPageProps, 'loading' | 'notFoundMessage'> => {
  const severityLevel = malwareSeverityToLevel(item.severity);
  const detectionId = getMalwareDetectionId(item) || item.wazuhAlertId || '—';
  const familyOrType = item.malwareFamily?.trim() || item.malwareType || '—';

  return {
    backLink: {
      label: 'Back to Malware Detection',
      to: getSecurityNavItemPath('malware-detection') ?? '',
    },
    title: formatMalwareThreatTitle(item.threatName),
    badges: (
      <EdrSeverityPill level={severityLevel} label={toMalwareTitle(item.severity) || '—'} />
    ),
    metaLine: (
      <>
        {detectionId}
        {' · '}
        {formatEdrDateTime(item.timestamp)}
        {' · '}
        <strong style={{ color: EDR_COLORS.textPrimary }}>{item.endpoint || '—'}</strong>
      </>
    ),
    main: (
      <>
        <EdrDetailSectionCard
          icon={<BugReportIcon style={{ fontSize: 16 }} />}
          title="Threat Overview"
        >
          <EdrDetailKeyValueRows
            rows={[
              {
                label: 'Threat Name',
                value: item.threatName ? (
                  <EdrExpandableText text={item.threatName} lines={2} />
                ) : (
                  '—'
                ),
              },
              { label: 'Family / Type', value: familyOrType },
              { label: 'Severity', value: toMalwareTitle(item.severity) || '—' },
              { label: 'Detection Time', value: <EdrDateTime value={item.timestamp} /> },
            ]}
          />
        </EdrDetailSectionCard>

        <EdrDetailSectionCard icon={<SecurityIcon style={{ fontSize: 16 }} />} title="Rule Details">
          {item.rule?.description ? (
            <Box mb={1.5}>
              <EdrExpandableText text={item.rule.description} lines={3} />
            </Box>
          ) : null}
          <EdrDetailKeyValueRows
            rows={[
              {
                label: 'Rule ID',
                value: item.rule?.id != null ? String(item.rule.id) : '—',
                mono: true,
              },
              {
                label: 'Level',
                value: typeof item.rule?.level === 'number' ? String(item.rule.level) : '—',
              },
              {
                label: 'Groups',
                value: item.rule?.groups?.length ? item.rule.groups.join(', ') : '—',
              },
            ]}
          />
        </EdrDetailSectionCard>
      </>
    ),
    sidebar: (
      <EdrDetailEndpointSidebar
        endpoint={item.endpoint || '—'}
        organization={item.organization}
      />
    ),
  };
};
